// src/components/LocationForm.js
import React from "react";
import LoadingSpinner from "../../components/LoadingSpinner";

const FormField = ({
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  required = true,
  disabled = false,
}) => (
  <div className="flex flex-col">
    <label className="font-semibold text-gray-700 mb-2 text-sm">{label}</label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-100 placeholder-gray-400"
      required={required}
      disabled={disabled}
    />
  </div>
);

const TimeInput12Hour = ({ label, value, onChange, disabled }) => {
  // Parse value "HH:MM" into 12-hour format parts
  const parseTime = (timeStr) => {
    if (!timeStr) return { h: "12", m: "00", p: "AM" };
    const [hStr, mStr] = timeStr.split(":");
    let h = parseInt(hStr, 10);
    let p = "AM";
    if (h >= 12) {
      p = "PM";
      if (h > 12) h -= 12;
    } else if (h === 0) {
      h = 12;
    }
    return { h: h.toString(), m: mStr || "00", p };
  };

  const initial = parseTime(value);
  const [hour, setHour] = React.useState(initial.h);
  const [minute, setMinute] = React.useState(initial.m);
  const [period, setPeriod] = React.useState(initial.p);

  // Update effect
  React.useEffect(() => {
    let h24 = parseInt(hour, 10);
    if (period === "PM" && h24 < 12) h24 += 12;
    if (period === "AM" && h24 === 12) h24 = 0;
    
    // Format back to "HH:MM"
    const formatted = `${h24.toString().padStart(2, "0")}:${minute.padStart(2, "0")}`;
    if (formatted !== value) {
      onChange(formatted);
    }
  }, [hour, minute, period]);

  // Handle external value changes (e.g. form reset, edit load)
  React.useEffect(() => {
    const updated = parseTime(value);
    setHour(updated.h);
    setMinute(updated.m);
    setPeriod(updated.p);
  }, [value]);

  return (
    <div className="flex flex-col">
      <label className="font-semibold text-gray-700 mb-2 text-sm">{label}</label>
      <div className="flex space-x-2">
        <select
          value={hour}
          onChange={(e) => setHour(e.target.value)}
          disabled={disabled}
          className="border border-gray-300 rounded-lg px-2 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white disabled:bg-gray-100 flex-1"
        >
          {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
            <option key={h} value={h.toString()}>
              {h.toString().padStart(2, '0')}
            </option>
          ))}
        </select>
        <span className="flex items-center text-gray-500 font-bold">:</span>
        <select
          value={minute}
          onChange={(e) => setMinute(e.target.value)}
          disabled={disabled}
          className="border border-gray-300 rounded-lg px-2 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white disabled:bg-gray-100 flex-1"
        >
          {["00", "15", "30", "45"].map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          disabled={disabled}
          className="border border-gray-300 rounded-lg px-2 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white disabled:bg-gray-100 flex-1"
        >
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      </div>
    </div>
  );
};


const LocationForm = ({
  step,
  state,
  formData,
  isSubmitting,
  handleInputChange,
  handleFileChange,
  removeDocument,
  handleFormSubmit,
  isStep1Valid,
  isStep2Valid,
  setStep,
  updateState,
  handleStoreImagesChange,
  removeStoreImage,
}) => {
  const isApproved = state.isEditing && formData.status === "approved";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl z-10">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {state.isEditing ? "Edit Location" : "Add New Location"}
              </h2>
              <div className="flex items-center space-x-2 mt-1">
                <p className="text-sm text-gray-500">
                  {step === 1 ? "Step 1: Operational Details" : "Step 2: Business Verification"}
                </p>
                {isApproved && (
                  <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded-full font-medium">
                    Restricted Edit
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={() => updateState({ showPopup: false })}
              className="text-gray-400 hover:text-gray-600 text-2xl"
              disabled={isSubmitting}
            >
              ×
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleFormSubmit} className="p-6 space-y-6">
          {/* STEP 1: Location & Operations */}
          {step === 1 && (
            <>
              {/* Row 1: Identifiers */}
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  label="Location Name"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  placeholder="e.g., Downtown Store"
                  disabled={isApproved || isSubmitting}
                />
                <FormField
                  label="Location ID"
                  value={formData.locationId}
                  onChange={(e) => handleInputChange("locationId", e.target.value)}
                  placeholder="Unique ID"
                  disabled={isApproved || isSubmitting}
                />
              </div>

              {/* Row 2: Address */}
              <FormField
                label="Full Address"
                value={formData.address}
                onChange={(e) => handleInputChange("address", e.target.value)}
                placeholder="Street Address, Suite, etc."
                disabled={isApproved || isSubmitting}
              />

              {/* Row 3: City/State/Zip */}
              <div className="grid grid-cols-3 gap-4">
                <FormField
                  label="City"
                  value={formData.city}
                  onChange={(e) => handleInputChange("city", e.target.value)}
                  placeholder="City"
                  disabled={isApproved || isSubmitting}
                />
                <FormField
                  label="State"
                  value={formData.state}
                  onChange={(e) => handleInputChange("state", e.target.value)}
                  placeholder="State"
                  disabled={isApproved || isSubmitting}
                />
                <FormField
                  label="ZIP Code"
                  value={formData.zip}
                  onChange={(e) => handleInputChange("zip", e.target.value)}
                  placeholder="ZIP"
                  disabled={isApproved || isSubmitting}
                />
              </div>

              {/* Row 4: Contact */}
              <FormField
                label="Contact Number"
                type="tel"
                value={formData.contact}
                onChange={(e) => handleInputChange("contact", e.target.value)}
                placeholder="(123) 456-7890"
                disabled={isApproved || isSubmitting}
              />

              {/* Row 5: Operational Hours (New Fields) */}
              <div className="grid grid-cols-2 gap-4">
                <TimeInput12Hour
                  label="Opening Time"
                  value={formData.openTime || ""}
                  onChange={(val) => handleInputChange("openTime", val)}
                  disabled={isSubmitting}
                />
                <TimeInput12Hour
                  label="Closing Time"
                  value={formData.closeTime || ""}
                  onChange={(val) => handleInputChange("closeTime", val)}
                  disabled={isSubmitting}
                />
              </div>

              {/* Step 1 Actions */}
              <div className="flex space-x-4 pt-4 border-t border-gray-100 mt-4">
                <button
                  type="button"
                  onClick={() => updateState({ showPopup: false })}
                  className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="button" // Changed to button to prevent submit, handled by setStep
                  onClick={() => isStep1Valid() && setStep(2)}
                  disabled={!isStep1Valid()}
                  className={`flex-1 py-3 rounded-lg text-white font-semibold transition-colors ${
                    isStep1Valid()
                      ? "bg-blue-600 hover:bg-blue-700"
                      : "bg-gray-400 cursor-not-allowed"
                  }`}
                >
                  Next: Verification
                </button>
              </div>
            </>
          )}

          {/* STEP 2: Simplified Verification */}
          {step === 2 && (
            <>
              {/* Row 1: Legal Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  label="Legal Business Name"
                  value={formData.businessName}
                  onChange={(e) =>
                    handleInputChange("businessName", e.target.value)
                  }
                  placeholder="Registered LLC/Corp Name"
                  disabled={isApproved || isSubmitting}
                />
                <FormField
                  label="Tax ID / EIN"
                  value={formData.businessIdNumber}
                  onChange={(e) =>
                    handleInputChange("businessIdNumber", e.target.value)
                  }
                  placeholder="e.g., 12-3456789"
                  disabled={isApproved || isSubmitting}
                />
              </div>

              {/* Document Uploads */}
              <div className="mt-4">
                <h3 className="text-sm font-semibold text-gray-800 mb-3">
                  Required Documents
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {["ein", "license", "proof"].map((slotId) => {
                    const slot =
                      formData.verificationDocuments.find(
                        (d) => d.id === slotId
                      ) || {
                        id: slotId,
                        type:
                          slotId === "ein"
                            ? "EIN Cert"
                            : slotId === "license"
                            ? "Business License"
                            : "Address Proof",
                        file: null,
                        previewUrl: null,
                        name: null,
                      };

                    return (
                      <div
                        key={slotId}
                        className="flex flex-col bg-gray-50 rounded-lg border border-gray-200 p-4"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-xs font-bold text-gray-700 uppercase">
                            {slot.type}
                          </span>
                          {(slot.file || slot.previewUrl) && !isApproved && !isSubmitting ? (
                            <button
                              type="button"
                              onClick={() => removeDocument(slotId)}
                              className="text-xs text-red-600 hover:underline"
                            >
                              ✕
                            </button>
                          ) : null}
                        </div>

                        <div className="flex-1 flex flex-col items-center justify-center text-center">
                          {slot.previewUrl ? (
                            <img
                              src={slot.previewUrl}
                              alt="preview"
                              className="h-20 object-contain rounded"
                            />
                          ) : slot.name ? (
                            <span className="text-xs text-gray-600 truncate max-w-full">
                              {slot.name}
                            </span>
                          ) : !isApproved && !isSubmitting ? (
                            <label className="cursor-pointer bg-white border border-gray-300 rounded px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 hover:border-blue-400 transition-all w-full">
                              Upload
                              <input
                                type="file"
                                accept="image/*,application/pdf"
                                onChange={(e) => handleFileChange(e, slotId)}
                                className="hidden"
                              />
                            </label>
                          ) : (
                            <span className="text-xs text-gray-400 italic">
                              {isSubmitting ? "Uploading..." : "No document"}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Store Images Section */}
              <div className="mt-4">
                <h3 className="text-sm font-semibold text-gray-800 mb-2">
                  Store Photos
                </h3>
                <div className={`border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-50 text-center ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  {!isSubmitting ? (
                    <label className="cursor-pointer text-blue-600 text-sm font-medium hover:text-blue-700">
                      + Add Images
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleStoreImagesChange}
                        className="hidden"
                      />
                    </label>
                  ) : (
                    <span className="text-gray-500 text-sm font-medium">Processing images...</span>
                  )}
                </div>

                {formData.storeImages?.length > 0 && (
                  <div className="grid grid-cols-4 gap-2 mt-3">
                    {formData.storeImages.map((img, idx) => (
                      <div
                        key={idx}
                        className="relative border border-gray-200 rounded overflow-hidden h-20 group"
                      >
                        <img
                          src={img.previewUrl}
                          alt="Store"
                          className="w-full h-full object-cover"
                        />
                        {!isSubmitting && (
                          <button
                            type="button"
                            onClick={() => removeStoreImage(idx)}
                            className="absolute top-0 right-0 bg-red-500 text-white w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Step 2 Actions */}
              <div className="flex space-x-4 pt-6 mt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={isSubmitting}
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={!isStep2Valid() || isSubmitting}
                  className={`flex-1 py-3 rounded-lg text-white font-semibold transition-colors flex justify-center items-center ${
                    !isStep2Valid() || isSubmitting
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700"
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <LoadingSpinner size="w-5 h-5" className="border-white" />
                      <span className="ml-2">Saving...</span>
                    </>
                  ) : (
                    "Submit Location"
                  )}
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default LocationForm;
export { FormField };